
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight, ChevronLeft, FileUp, Check, Save } from 'lucide-react';

// Form schema
const formSchema = z.object({
  title: z.string().min(3, { message: 'Titeln måste vara minst 3 tecken.' }),
  authority: z.string().min(2, { message: 'Beställaren måste anges.' }),
  procurementType: z.string().min(1, { message: 'Upphandlingstyp måste väljas.' }),
  requirements: z.string().optional(),
  specialConditions: z.string().optional(),
  budget: z.string().optional(),
  deadline: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const NewProcurement = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const supabase = useSupabaseClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      authority: '',
      procurementType: '',
      requirements: '',
      specialConditions: '',
      budget: '',
      deadline: '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    
    try {
      // Store the data in Supabase
      const { data, error } = await supabase
        .from('procurements')
        .insert([
          {
            title: values.title,
            authority: values.authority,
            procurement_type: values.procurementType,
            requirements: values.requirements,
            special_conditions: values.specialConditions,
            budget: values.budget || null,
            deadline: values.deadline || null,
            status: 'Ny', // Initial status
          }
        ])
        .select();

      if (error) throw error;

      toast({
        title: "Upphandling sparad",
        description: "Din upphandling har lagts till och är redo för analys.",
      });
      
      // Navigate to the procurements page
      navigate(`/procurements`);
    } catch (error: any) {
      console.error('Error submitting procurement:', error);
      toast({
        title: "Ett fel uppstod",
        description: error.message || "Kunde inte spara upphandlingen. Försök igen.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToNextStep = () => {
    // Basic validation for each step
    if (currentStep === 1) {
      const basicInfoValid = form.trigger(['title', 'authority', 'procurementType']);
      if (basicInfoValid) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep(currentStep > 1 ? currentStep - 1 : 1);
  };

  return (
    <DashboardLayout>
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-darkgray mb-2">Ny upphandling</h1>
          <p className="text-gray-600">
            Fyll i information om upphandlingen för att få en detaljerad analys.
          </p>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3].map((step) => (
              <div 
                key={step} 
                className={`flex-1 relative ${
                  step < 3 ? 'after:content-[""] after:absolute after:top-1/2 after:left-1/2 after:w-full after:h-0.5 after:bg-gray-300 after:-z-10' : ''
                }`}
              >
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    currentStep === step 
                      ? 'bg-yellow-500 border-yellow-500 text-white'
                      : currentStep > step
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'bg-white border-gray-300 text-gray-500'
                  }`}>
                    {currentStep > step ? <Check size={18} /> : step}
                  </div>
                  <span className="text-xs mt-2 text-center">
                    {step === 1 ? 'Grundinformation' : step === 2 ? 'Detaljer' : 'Dokument & Sammanfattning'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {currentStep === 1 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Upphandlingens titel *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="T.ex. Nybyggnation av skola i Halmstad" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="authority"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Beställare/Myndighet *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="T.ex. Halmstad kommun" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="procurementType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Upphandlingstyp *</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Välj upphandlingstyp" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="construction">Bygg & Anläggning</SelectItem>
                              <SelectItem value="it">IT & Digitalisering</SelectItem>
                              <SelectItem value="services">Tjänster</SelectItem>
                              <SelectItem value="supplies">Varor & Utrustning</SelectItem>
                              <SelectItem value="healthcare">Vård & Omsorg</SelectItem>
                              <SelectItem value="consulting">Konsulttjänster</SelectItem>
                              <SelectItem value="other">Övrigt</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="budget"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Budget</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="T.ex. 1 500 000 kr" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="deadline"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Deadline</FormLabel>
                          <FormControl>
                            <Input {...field} type="date" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {currentStep === 2 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="requirements"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Specifika krav</FormLabel>
                          <FormControl>
                            <Textarea 
                              {...field} 
                              placeholder="Lista viktiga krav och kvalifikationer..." 
                              rows={5}
                            />
                          </FormControl>
                          <FormDescription>
                            T.ex. certifieringar, erfarenheter, tekniska specifikationer
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="specialConditions"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Särskilda villkor</FormLabel>
                          <FormControl>
                            <Textarea 
                              {...field} 
                              placeholder="Ange eventuella särskilda villkor..." 
                              rows={3}
                            />
                          </FormControl>
                          <FormDescription>
                            T.ex. miljökrav, sociala krav, LOU-specifika villkor
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {currentStep === 3 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <FileUp className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-semibold text-darkgray">Ladda upp dokument</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        Dra och släpp filer här, eller klicka för att bläddra
                      </p>
                      <div className="mt-4">
                        <Input
                          type="file"
                          id="file-upload"
                          className="hidden"
                          multiple
                          onChange={() => {
                            // Handle file upload in a real app
                            toast({
                              title: "Filuppladdning",
                              description: "Filer laddas upp...",
                            });
                          }}
                        />
                        <label htmlFor="file-upload">
                          <Button
                            type="button"
                            variant="outline"
                            className="relative cursor-pointer"
                            size="sm"
                          >
                            Välj filer
                          </Button>
                        </label>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Stöder PDF, Word, Excel (max 10MB per fil)
                      </p>
                    </div>

                    <div className="bg-yellow-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold mb-4">Sammanfattning av upphandlingen</h3>
                      
                      <Tabs defaultValue="basic" className="w-full">
                        <TabsList className="mb-4">
                          <TabsTrigger value="basic">Grundinformation</TabsTrigger>
                          <TabsTrigger value="details">Detaljer</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="basic" className="space-y-4">
                          <div>
                            <span className="font-medium">Titel:</span>
                            <span className="ml-2">{form.watch('title')}</span>
                          </div>
                          <div>
                            <span className="font-medium">Beställare:</span>
                            <span className="ml-2">{form.watch('authority')}</span>
                          </div>
                          <div>
                            <span className="font-medium">Upphandlingstyp:</span>
                            <span className="ml-2">
                              {form.watch('procurementType') === 'construction' && 'Bygg & Anläggning'}
                              {form.watch('procurementType') === 'it' && 'IT & Digitalisering'}
                              {form.watch('procurementType') === 'services' && 'Tjänster'}
                              {form.watch('procurementType') === 'supplies' && 'Varor & Utrustning'}
                              {form.watch('procurementType') === 'healthcare' && 'Vård & Omsorg'}
                              {form.watch('procurementType') === 'consulting' && 'Konsulttjänster'}
                              {form.watch('procurementType') === 'other' && 'Övrigt'}
                            </span>
                          </div>
                          {form.watch('budget') && (
                            <div>
                              <span className="font-medium">Budget:</span>
                              <span className="ml-2">{form.watch('budget')}</span>
                            </div>
                          )}
                          {form.watch('deadline') && (
                            <div>
                              <span className="font-medium">Deadline:</span>
                              <span className="ml-2">{form.watch('deadline')}</span>
                            </div>
                          )}
                        </TabsContent>
                        
                        <TabsContent value="details" className="space-y-4">
                          {form.watch('requirements') && (
                            <div>
                              <span className="font-medium">Specifika krav:</span>
                              <p className="mt-1 text-sm">
                                {form.watch('requirements')}
                              </p>
                            </div>
                          )}
                          
                          {form.watch('specialConditions') && (
                            <div>
                              <span className="font-medium">Särskilda villkor:</span>
                              <p className="mt-1 text-sm">
                                {form.watch('specialConditions')}
                              </p>
                            </div>
                          )}
                        </TabsContent>
                      </Tabs>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-between pt-4">
              {currentStep > 1 ? (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={goToPreviousStep}
                  disabled={isSubmitting}
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Föregående
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Avbryt
                </Button>
              )}

              {currentStep < 3 ? (
                <Button 
                  type="button"
                  onClick={goToNextStep}
                >
                  Nästa
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>Sparar...</>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Spara upphandling
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </DashboardLayout>
  );
};

export default NewProcurement;
